'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  BarChart3,
  CheckCircle2,
  CircleMinus,
  CreditCard,
  Download,
  FileSpreadsheet,
  Megaphone,
  Network,
  Percent,
  ReceiptText,
  RotateCcw,
  ShoppingCart,
  TriangleAlert,
  Truck,
} from 'lucide-react';

import {
  DEFAULT_CURRENCY,
  formatMoney,
  getCurrencyName,
  getCurrencySymbol,
} from '@kit/shared/currency';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';
import {
  type DropshippingProfitInputs,
  calculateDropshippingProfit,
  normalizeDropshippingProfitInput,
} from './_lib/calculate-dropshipping-profit';

const baseUrl = appConfig.url;
const calculatorUrl = `${baseUrl}/tools/dropshipping-profit-calculator`;
const toolsUrl = `${baseUrl}/tools`;
const templateUrl = '/tools/templates/dropshipping-profit-loss-template';
type FormData = Record<keyof DropshippingProfitInputs, string>;

const DEFAULT_FORM_DATA: FormData = {
  adSpendPercent: '25',
  appsCost: '1.50',
  monthlyOrders: '400',
  productCost: '8.50',
  returnsPercent: '3',
  sellingPrice: '34.99',
  shippingCost: '4.25',
  transactionFeeFixed: '0.30',
  transactionFeePercent: '2.9',
};

const faqItems = [
  {
    value: 'realistic-profit',
    question:
      'What is a realistic dropshipping profit on a $34.99 order with an $8.50 product cost?',
    answer:
      'The page opens with a $34.99 order: $8.50 product, $4.25 shipping, 2.9% + $0.30 processing, 3% returns, and $1.50 apps. That leaves $18.38 before ads and $9.63 after a 25% ad spend (27.5% margin, 1.90x break-even ROAS). At 400 orders a month that is $3,852. Replace every field. A reported 4x ROAS can still lose money if the other costs are worse than this example.',
  },
  {
    value: 'free',
    question: 'Is this dropshipping profit calculator free?',
    answer:
      'Yes. It runs entirely in your browser with no signup, no usage limit, and no credit card. Export any scenario to CSV, or use the free Excel P&L template for month-by-month tracking.',
  },
  {
    value: 'break-even-roas',
    question: 'What is break-even ROAS?',
    answer:
      'Break-even ROAS is the minimum return on ad spend, meaning revenue divided by ad spend, that leaves zero profit after every other cost. Spend that produces a lower ROAS than this loses money on the order; spend above it leaves a margin.',
  },
  {
    value: 'roas-vs-margin',
    question: 'Why does a 4x ROAS still lose money sometimes?',
    answer:
      'ROAS only measures revenue against ad spend. It ignores product cost, shipping, payment fees, and returns. A product with thin margins can need a much higher ROAS than a "healthy-looking" 3x or 4x to actually break even. This calculator shows you the number for your own product.',
  },
  {
    value: 'transaction-fees',
    question: 'What payment processing fee should I use?',
    answer:
      'Most card processors and checkout providers charge a percentage plus a small fixed fee per transaction, commonly around 2.9% + $0.30. Confirm the exact rate in your payment provider dashboard, because it varies by provider, card type, and country.',
  },
  {
    value: 'returns',
    question: 'Why include a returns and refund reserve?',
    answer:
      'Refunds, chargebacks, and disputes are a normal cost of running an online store even when you never physically receive the item back. A reserve turns that uneven expense into a realistic per-order allowance instead of a surprise at the end of the month.',
  },
  {
    value: 'apps-overhead',
    question: 'What counts as "apps and overhead" per order?',
    answer:
      'Take your monthly software costs, including store platform, apps, subscriptions, and virtual assistants, then divide by your monthly order count. That per-order figure belongs in the unit economics even though it is really a fixed cost.',
  },
] as const;

const economicsCards = [
  {
    title: 'Product and shipping cost',
    description:
      'Use the price the supplier actually charges per unit plus what it costs to ship that order to the customer.',
    icon: Truck,
  },
  {
    title: 'Payment processing',
    description:
      'Card and checkout fees are usually a percentage plus a fixed amount per order, and both add up fast at low order values.',
    icon: CreditCard,
  },
  {
    title: 'Advertising',
    description:
      'Model ad spend as a percentage of revenue and compare it with your break-even ROAS before scaling a campaign.',
    icon: Megaphone,
  },
  {
    title: 'Returns and overhead',
    description:
      'Reserve for refunds and chargebacks, and allocate apps, subscriptions, and other fixed costs across each order.',
    icon: ReceiptText,
  },
] as const;

function parseInput(value: string) {
  const parsed = Number.parseFloat(value.replaceAll(',', ''));
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

function formatPercent(value: number | null) {
  return value === null ? 'N/A' : `${value.toFixed(1)}%`;
}

function formatRoas(value: number | null) {
  return value === null ? 'N/A' : `${value.toFixed(2)}x`;
}

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Dropshipping Profit Calculator: After Ads, Fees, Shipping',
    alternateName: 'Ecommerce Profit and Loss Calculator',
    description:
      'Calculate dropshipping profit after product cost, shipping, payment fees, ads, and returns. See margin, ROI, and break-even ROAS.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    creator: { '@type': 'Organization', name: appConfig.name, url: baseUrl },
    featureList: [
      'Calculate dropshipping profit per order',
      'Estimate monthly ecommerce profit',
      'Calculate profit margin and ROI',
      'Calculate break-even ROAS',
      'Model product cost, shipping, payment fees, advertising, and returns',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Free Tools', item: toolsUrl },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Dropshipping Profit Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function CalculatorInput({
  help,
  id,
  label,
  onChange,
  prefix,
  prefixCurrency,
  step = '0.01',
  suffix,
  value,
}: {
  help?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  prefix?: string;
  /** ISO code announced to screen readers, since several currencies share "$". */
  prefixCurrency?: string;
  step?: string;
  suffix?: string;
  value: string;
}) {
  const helpId = help ? `${id}-help` : undefined;
  const unitId = prefix || suffix ? `${id}-unit` : undefined;
  const describedBy = [unitId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-foreground text-sm font-bold">
        {label}
      </Label>
      <div className="relative">
        {prefix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-sm"
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={id}
          aria-describedby={describedBy}
          type="number"
          min="0"
          max={suffix === '%' ? '100' : undefined}
          step={step}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={`border-border bg-muted h-12 rounded-xl shadow-none ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-9' : ''}`}
        />
        {suffix ? (
          <span
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-sm"
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {unitId ? (
        <span id={unitId} className="sr-only">
          {prefixCurrency
            ? `Amount in ${getCurrencyName(prefixCurrency)}.`
            : null}
          {suffix === '%' ? 'Percentage from 0 to 100.' : null}
        </span>
      ) : null}
      {help ? (
        <p id={helpId} className="text-muted-foreground text-xs leading-5">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export default function DropshippingProfitCalculatorPage() {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const formatCurrency = (value: number) => formatMoney({ currency, value });
  const currencySymbol = getCurrencySymbol(currency);
  const startFreePath = pathsConfig.auth.startFree;
  const inputs: DropshippingProfitInputs = {
    adSpendPercent: parseInput(formData.adSpendPercent),
    appsCost: parseInput(formData.appsCost),
    monthlyOrders: parseInput(formData.monthlyOrders),
    productCost: parseInput(formData.productCost),
    returnsPercent: parseInput(formData.returnsPercent),
    sellingPrice: parseInput(formData.sellingPrice),
    shippingCost: parseInput(formData.shippingCost),
    transactionFeeFixed: parseInput(formData.transactionFeeFixed),
    transactionFeePercent: parseInput(formData.transactionFeePercent),
  };
  const result = calculateDropshippingProfit(inputs);
  const isProfitable = result.profitPerOrder > 0;
  const isLoss = result.profitPerOrder < 0;

  function updateField(key: keyof FormData, value: string) {
    setFormData((current) => ({
      ...current,
      [key]: normalizeDropshippingProfitInput(key, value),
    }));
  }

  function resetExample() {
    setFormData(DEFAULT_FORM_DATA);
  }

  function downloadReport() {
    const rows = [
      ['Metric', 'Value'],
      ['Selling price', inputs.sellingPrice.toFixed(2)],
      ['Product cost per unit', inputs.productCost.toFixed(2)],
      ['Shipping cost per order', inputs.shippingCost.toFixed(2)],
      ['Transaction fee percent', inputs.transactionFeePercent.toFixed(2)],
      ['Transaction fee fixed', inputs.transactionFeeFixed.toFixed(2)],
      ['Ad spend percent', inputs.adSpendPercent.toFixed(2)],
      ['Returns reserve percent', inputs.returnsPercent.toFixed(2)],
      ['Apps and overhead per order', inputs.appsCost.toFixed(2)],
      ['Monthly orders', inputs.monthlyOrders.toFixed(0)],
      ['Profit per order', result.profitPerOrder.toFixed(2)],
      ['Profit margin percent', result.profitMarginPercent.toFixed(1)],
      [
        'ROI percent',
        result.roiPercent === null ? 'N/A' : result.roiPercent.toFixed(1),
      ],
      [
        'Break-even ROAS',
        result.breakEvenRoas === null ? 'N/A' : result.breakEvenRoas.toFixed(2),
      ],
      ['Monthly revenue', result.monthlyRevenue.toFixed(2)],
      ['Monthly profit', result.monthlyProfit.toFixed(2)],
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `dropshipping-profit-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const costRows = [
    { label: 'Product cost', value: inputs.productCost },
    { label: 'Shipping cost', value: inputs.shippingCost },
    { label: 'Payment processing', value: result.transactionFee },
    { label: 'Advertising', value: result.adSpend },
    { label: 'Returns reserve', value: result.returnsReserve },
    { label: 'Apps and overhead', value: inputs.appsCost },
  ];

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[480px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.13*100%),transparent),_transparent_44%)]" />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="max-w-2xl">
                <div className="bg-primary/10 text-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <ShoppingCart className="h-4 w-4" />
                  Free ecommerce seller tool
                </div>

                <h1 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className="text-primary">
                    Dropshipping Profit Calculator
                  </span>
                  : after ads, fees, and shipping
                </h1>
                <p className="text-muted-foreground mt-6 max-w-xl text-lg leading-8">
                  A $34.99 order with an $8.50 product and $4.25 shipping looks
                  like a $22 spread. After 2.9% + $0.30, 3% returns, $1.50 apps,
                  and 25% ads, this page leaves <strong>$9.63</strong> (27.5%)
                  and a 1.90x break-even ROAS. A 4x ROAS can still lose money if
                  the other costs are worse.
                </p>

                <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                  {[
                    'Includes ads, fees, shipping, and returns',
                    'Shows break-even ROAS, not just margin',
                    'No signup required',
                  ].map((item) => (
                    <span
                      key={item}
                      className="text-muted-foreground inline-flex items-center gap-2 text-sm font-medium"
                    >
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ))}
                </div>

                <p className="text-muted-foreground mt-8 text-sm leading-6">
                  The calculator opens with a typical dropshipping example.
                  Replace every field with your own product numbers to model
                  your real unit economics.
                </p>
              </div>

              <div
                aria-live="polite"
                className="bg-primary text-on-primary rounded-[10px] p-7 shadow-[0_24px_70px_-30px_rgba(38,31,28,0.7)] sm:p-9"
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-on-primary text-xs font-bold tracking-[0.18em] uppercase">
                      Estimated profit per order
                    </p>
                    {/* No colour shift for the loss state: every red dark
                        enough to read on this coral is illegible, and no
                        lighter one clears 3:1. The minus sign and the icon
                        swap below carry the signal instead. */}
                    <p className="mt-3 text-5xl font-extrabold tracking-tight tabular-nums">
                      {formatCurrency(result.profitPerOrder)}
                    </p>
                  </div>
                  <div className="bg-on-primary/10 rounded-2xl p-3">
                    {isProfitable ? (
                      <BarChart3 className="h-7 w-7" />
                    ) : isLoss ? (
                      <TriangleAlert className="h-7 w-7" />
                    ) : (
                      <CircleMinus className="h-7 w-7" />
                    )}
                  </div>
                </div>

                <div className="border-on-primary/20 mt-8 grid grid-cols-2 gap-4 border-t pt-7">
                  {[
                    [
                      'Profit margin',
                      formatPercent(result.profitMarginPercent),
                    ],
                    ['ROI on unit cost', formatPercent(result.roiPercent)],
                    ['Break-even ROAS', formatRoas(result.breakEvenRoas)],
                    ['Monthly profit', formatCurrency(result.monthlyProfit)],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-on-primary/10 rounded-xl p-4"
                    >
                      <p className="text-on-primary text-xs">{label}</p>
                      <p className="mt-1 text-lg font-bold tabular-nums">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 text-sm">
                  <span className="text-on-primary">Profit before ads</span>
                  <span className="font-bold tabular-nums">
                    {formatCurrency(result.preAdSpendProfit)}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-border bg-card mt-12 rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8 lg:p-10">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-extrabold">
                    Calculate dropshipping profit and loss
                  </h2>
                  <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-6">
                    The defaults are an illustrative example with realistic
                    planning allowances. Replace them with your exact product
                    numbers before scaling ad spend on a campaign.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetExample}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset example
                  </Button>
                  <Button type="button" onClick={downloadReport}>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </div>
              </div>

              <form
                onSubmit={(event) => event.preventDefault()}
                className="mt-8 grid gap-x-6 gap-y-6 md:grid-cols-2 lg:grid-cols-3"
              >
                <CurrencyPicker
                  hint="Enter every figure in this currency."
                  onChange={setCurrency}
                  value={currency}
                />

                <CalculatorInput
                  id="sellingPrice"
                  label="Selling price"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  value={formData.sellingPrice}
                  onChange={(value) => updateField('sellingPrice', value)}
                />
                <CalculatorInput
                  id="productCost"
                  label="Product cost per unit"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="What the supplier charges per unit"
                  value={formData.productCost}
                  onChange={(value) => updateField('productCost', value)}
                />
                <CalculatorInput
                  id="shippingCost"
                  label="Shipping cost per order"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="What it costs to ship to the customer"
                  value={formData.shippingCost}
                  onChange={(value) => updateField('shippingCost', value)}
                />
                <CalculatorInput
                  id="transactionFeePercent"
                  label="Payment processing fee"
                  suffix="%"
                  help="Card/checkout fee, often around 2.9%"
                  value={formData.transactionFeePercent}
                  onChange={(value) =>
                    updateField('transactionFeePercent', value)
                  }
                />
                <CalculatorInput
                  id="transactionFeeFixed"
                  label="Payment fee (fixed)"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Flat fee per transaction, often $0.30"
                  value={formData.transactionFeeFixed}
                  onChange={(value) =>
                    updateField('transactionFeeFixed', value)
                  }
                />
                <CalculatorInput
                  id="adSpendPercent"
                  label="Advertising cost"
                  suffix="%"
                  help="Ad spend as a percentage of sales"
                  value={formData.adSpendPercent}
                  onChange={(value) => updateField('adSpendPercent', value)}
                />
                <CalculatorInput
                  id="returnsPercent"
                  label="Returns and refund reserve"
                  suffix="%"
                  help="Allowance for refunds and chargebacks"
                  value={formData.returnsPercent}
                  onChange={(value) => updateField('returnsPercent', value)}
                />
                <CalculatorInput
                  id="appsCost"
                  label="Apps and overhead per order"
                  prefix={currencySymbol}
                  prefixCurrency={currency}
                  help="Monthly software cost ÷ monthly orders"
                  value={formData.appsCost}
                  onChange={(value) => updateField('appsCost', value)}
                />
                <CalculatorInput
                  id="monthlyOrders"
                  label="Orders per month"
                  step="1"
                  value={formData.monthlyOrders}
                  onChange={(value) => updateField('monthlyOrders', value)}
                />
              </form>

              <div className="border-border mt-8 flex flex-col gap-5 border-t pt-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 text-primary hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex">
                    <Network className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-base font-bold">
                      This estimate is only as good as your product cost.
                    </p>
                    <p className="text-muted-foreground mt-1 max-w-xl text-sm leading-6">
                      {appConfig.name} tracks the real cost of every purchase
                      order, including supplier price, freight, duty, and
                      fulfillment, so the number you model here matches what you
                      actually pay.
                    </p>
                  </div>
                </div>
                <Button
                  asChild
                  className="h-11 shrink-0 rounded-xl px-6 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card border-border border-b px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                  Worked example
                </p>
                <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
                  What does a {formatCurrency(inputs.sellingPrice)} dropshipping
                  order actually leave?
                </h2>
                <p className="text-muted-foreground mt-5 text-base leading-8">
                  With the current inputs, selling price minus product and
                  shipping cost leaves a{' '}
                  {formatCurrency(
                    inputs.sellingPrice -
                      inputs.productCost -
                      inputs.shippingCost,
                  )}{' '}
                  preliminary spread. After payment fees, advertising, and
                  returns, the estimated profit is{' '}
                  <strong>{formatCurrency(result.profitPerOrder)}</strong> per
                  order.
                </p>
                <div className="border-warn-fg/25 bg-warn-bg text-warn-fg mt-6 rounded-2xl border p-5 text-sm leading-6">
                  <div className="flex gap-3">
                    <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                    <p>
                      A campaign reporting a 3x or 4x ROAS can still lose money
                      on thin-margin products. Compare your actual ROAS against
                      the break-even ROAS above before you scale spend.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-border bg-muted rounded-3xl border p-6 sm:p-8">
                <div className="flex items-center justify-between border-b pb-4">
                  <span className="font-bold">Selling price</span>
                  <span className="text-xl font-extrabold tabular-nums">
                    {formatCurrency(inputs.sellingPrice)}
                  </span>
                </div>
                <div className="mt-3 space-y-1">
                  {costRows.map((row) => (
                    <div
                      key={row.label}
                      className="text-muted-foreground flex items-center justify-between py-2 text-sm"
                    >
                      <span>{row.label}</span>
                      <span className="tabular-nums">
                        {row.value > 0 ? '-' : ''}
                        {formatCurrency(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-border mt-4 flex items-end justify-between border-t pt-5">
                  <div>
                    <p className="text-sm font-bold">Estimated profit</p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {formatPercent(result.profitMarginPercent)} margin
                    </p>
                  </div>
                  <span
                    className={`text-3xl font-extrabold tabular-nums ${
                      isProfitable
                        ? 'text-primary'
                        : isLoss
                          ? 'text-destructive'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {formatCurrency(result.profitPerOrder)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                Dropshipping unit economics
              </p>
              <h2 className="font-heading mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
                Costs a dropshipping P&amp;L calculator should include
              </h2>
              <p className="text-muted-foreground mt-5 text-base leading-8">
                Revenue minus product cost is not profit. A useful ecommerce
                model includes the costs that move with each order and the
                reserves that appear unevenly over time.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {economicsCards.map((item) => {
                const Icon = item.icon;
                return (
                  <article
                    key={item.title}
                    className="border-border bg-card rounded-2xl border p-6 shadow-sm"
                  >
                    <div className="bg-primary/10 text-primary flex h-11 w-11 items-center justify-center rounded-xl">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold">{item.title}</h3>
                    <p className="text-muted-foreground mt-2 text-sm leading-7">
                      {item.description}
                    </p>
                  </article>
                );
              })}
            </div>

            <div className="border-border bg-card mt-12 rounded-3xl border p-7 sm:p-10">
              <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
                <div>
                  <div className="bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-xl">
                    <Percent className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-2xl font-extrabold">
                    The profit formula
                  </h3>
                  <p className="text-muted-foreground mt-3 text-sm leading-7">
                    Break-even ROAS is calculated from the profit left before
                    advertising, divided by ad spend at the point profit hits
                    zero.
                  </p>
                </div>
                <div className="bg-muted border-border rounded-2xl border p-6 text-center">
                  <p className="font-mono text-base leading-8 font-bold sm:text-lg">
                    Selling price - product cost - shipping - fees - ads -
                    returns - overhead = profit
                  </p>
                  <p className="text-primary mt-4 text-sm font-bold">
                    Current break-even ROAS: {formatRoas(result.breakEvenRoas)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card border-border border-y px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="grid gap-10 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <h2 className="font-heading text-3xl font-extrabold tracking-tight">
                  Track it month over month
                </h2>
                <p className="text-muted-foreground mt-4 text-base leading-8">
                  This calculator models a single order. When you are ready to
                  track real orders across a month, with your own SKUs,
                  channels, and ad campaigns, use the free Excel P&amp;L
                  template with the same formulas already wired up.
                </p>
              </div>
              <Button asChild size="lg" className="h-12 rounded-xl px-6">
                <Link href={templateUrl}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Get the Excel Template
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section id="faq" className="px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
              Dropshipping profit calculator FAQ
            </h2>
            <div className="mt-10">
              <Accordion
                type="single"
                collapsible
                defaultValue="realistic-profit"
              >
                {faqItems.map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="border-border bg-card mb-4 overflow-hidden rounded-2xl border px-6"
                  >
                    <AccordionTrigger className="py-6 text-left text-base font-bold hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculator is free. The margin is the hard part.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                A spreadsheet estimate is not a landed cost.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                Every number above depends on what your unit truly costs
                delivered to your fulfillment partner or 3PL. {appConfig.name}{' '}
                keeps purchase orders, suppliers, documents, freight, duty, and
                prep in one workspace, so your real cost per unit updates as the
                order moves, not three months later.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See how it works</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle2 className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {appConfig.name} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{appConfig.name}</p>
                      <p className="text-muted-foreground text-xs">
                        Example dropshipping restock
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    On track
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        Purchase order SA-1048
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $14.06
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Actual cost per order, tracked automatically
                      </p>
                    </div>
                    <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      2,000 units
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[82%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Order placed</span>
                    <span>Ready to fulfill</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <ShoppingCart className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$8.50</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Product cost per unit
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Truck className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$4.25</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Shipping per order
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <CreditCard className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$1.31</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Payment fee per order
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle2 className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Drop this into the calculator instead of guessing at
                      product cost.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>

        <section className="px-4 pb-20 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl">
            <h2 className="text-foreground text-2xl font-bold">
              Related reading
            </h2>
            <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
              <li>
                <Link
                  href="/tools/templates/dropshipping-profit-loss-template"
                  className="text-primary font-semibold hover:underline"
                >
                  Dropshipping profit and loss template
                </Link>
                : the same per-order math, tracked month by month in Excel.
              </li>
              <li>
                <Link
                  href="/blog/how-to-calculate-shopify-landed-cost"
                  className="text-primary font-semibold hover:underline"
                >
                  How to calculate Shopify landed cost
                </Link>
                : inventory cost, not a checkout duty widget. A $20 factory
                price that lands at $27.
              </li>
              <li>
                <Link
                  href="/tools/profit-margin-calculator"
                  className="text-primary font-semibold hover:underline"
                >
                  Profit margin calculator
                </Link>
                : gross vs net and the price that hits a target when the channel
                is not ads-first.
              </li>
            </ul>
          </div>
          <RelatedTools currentPath="/tools/dropshipping-profit-calculator" />
        </section>
      </div>
    </>
  );
}
